import * as React from 'react';
import { connect } from 'react-redux';
import { default as DispatchProps, mapDispatchToProps } from '../../../application/core/DispatchProps';
import './IntegrationPanel.css';
import { getBackend } from '../../../application/core/createStore';
import { AjaxResponse } from 'rxjs/ajax';
import api from '../../../application/core/api';
import OutwardIntegration, { Type } from './OutwardIntegration';
import { catchError, map } from 'rxjs/internal/operators';
import RequestFailed from '../../../application/core/events/RequestFailed';
import { AjaxError } from 'rxjs/internal/observable/dom/AjaxObservable';
import { of } from 'rxjs/index';
import Loader from '../../common/Loader';
import OutwardIntegrationResult, {
    OutwardEstimationIntegrationResult,
    OutwardOrderIntegrationResult
} from '../../../domain/OutwardIntegrationResult';
import TableRow from '../TableRow';

interface State {
    orders: OutwardIntegrationResult[];
    estimations: OutwardIntegrationResult[];
    loader: boolean;
    selectedOrdersFilter: string;
    selectedEstimationsFilter: string;
}

class IntegrationPanel extends React.PureComponent<DispatchProps, State> {
    constructor(props: DispatchProps) {
        super(props);
        this.state = {
            orders: [],
            estimations: [],
            loader: true,
            selectedOrdersFilter: 'rejected',
            selectedEstimationsFilter: 'all'
        };
    }

    public componentDidMount(): void {
        this.getRecords('orders', 'rejected');
        this.getRecords('estimations', 'all');
    }

    public render(): JSX.Element {
        return (
            <div className="integration-panel">
                <div className="panel-tables">
                    {this.state.loader && <Loader />}
                    <div className="panel-table">
                        <OutwardIntegration
                            label="Orders"
                            type="orders"
                            filters={[
                                'all',
                                'open',
                                'pending',
                                'created',
                                'complete',
                                'processing',
                                'shipped',
                                'packed',
                                'packingInProgress',
                                'rejected'
                            ]}
                            headers={['Order #', 'Customer ID', 'Status', 'Place date', 'Send time', '']}
                            filterChanged={this.onFilterChange}
                            items={this.state.orders}
                            selected={this.state.selectedOrdersFilter}
                            row={this.orderRow}
                        />
                    </div>
                    <div className="panel-table">
                        <OutwardIntegration
                            label="Estimations"
                            type="estimations"
                            filters={['all']}
                            headers={['Estimation #', 'Customer ID', 'Status', 'Place date', 'Send time', '']}
                            filterChanged={this.onFilterChange}
                            items={this.state.estimations}
                            row={this.estimationRow}
                            selected={this.state.selectedEstimationsFilter}
                        />
                    </div>
                </div>
            </div>
        );
    }

    private getRecords = (type: Type, status: string): void => {
        this.setLoader(true);
        getBackend()
            .get(`${api()}${type}/status?status=${status}`)
            .pipe(
                map((ajaxResponse: AjaxResponse) =>
                    ajaxResponse.response.sort((a: OutwardIntegrationResult, b: OutwardIntegrationResult) => {
                        return new Date(a.placeDate) > new Date(b.placeDate) ? -1 : 1;
                    })
                ),
                catchError((e: AjaxError) => {
                    this.props.dispatch(new RequestFailed(e.status));
                    return of([]);
                })
            )
            .subscribe((response: Array<OutwardOrderIntegrationResult | OutwardEstimationIntegrationResult>) => {
                this.setLoader(false);
                const items: OutwardIntegrationResult[] = response.map(
                    (item: OutwardOrderIntegrationResult | OutwardEstimationIntegrationResult) => {
                        // @ts-ignore
                        const { estimationId, orderId, ...rest } = item;
                        return {
                            ...rest,
                            id: estimationId || orderId
                        };
                    }
                );
                this.setState({
                    orders: type === 'orders' ? items : this.state.orders,
                    estimations: type === 'estimations' ? items : this.state.estimations
                });
            });
    };

    private retrySendOrder = (orderId: number): void => {
        this.setLoader(true);
        getBackend()
            .post(`${api()}order/retry/`, orderId, { 'Content-Type': 'text/plain' })
            .pipe(
                catchError((e: AjaxError) => {
                    this.setState({
                        loader: false
                    });
                    this.props.dispatch(new RequestFailed(e.status));
                    return of([]);
                })
            )
            .subscribe(this.getRecordsWithDelay);
    };

    private getRecordsWithDelay = (): void => {
        setTimeout(() => {
            this.setState(
                {
                    loader: false
                },
                () => {
                    this.getRecords('orders', this.state.selectedOrdersFilter);
                }
            );
        }, 200);
    };

    private setLoader = (value: boolean): void =>
        this.setState({
            loader: value
        });

    private onFilterChange = (value: string, type: Type): void => {
        switch (type) {
            case 'orders':
                this.setState({ selectedOrdersFilter: value });
                break;
            case 'estimations':
                this.setState({ selectedEstimationsFilter: value });
                break;
        }
        this.getRecords(type, value);
    };

    private orderRow = (orderData: OutwardIntegrationResult): JSX.Element => {
        return <TableRow data={orderData} type={'orders'} retrySendOrder={this.retrySendOrder} />;
    };

    private estimationRow = (orderData: OutwardIntegrationResult): JSX.Element => {
        return <TableRow data={orderData} type={'estimations'} />;
    };
}

export default connect(mapDispatchToProps)(IntegrationPanel);



// WEBPACK FOOTER //
// ./src/view/admin/integration/IntegrationPanel.tsx